import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomDateHeurePipe } from 'src/app/pipe/dateHeure.pipe';
import { UtilisateurListPilotageModel } from 'src/models/pilotage/utilisateurListPilotageModel';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MoveUserRootDialogComponent } from './move-user-root-dialog/move-user-root-dialog.component';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';

@Component({
    selector: 'app-utilisateurs-root-managment',
    imports: [
        MatProgressSpinnerModule,
        TranslateModule,
        CommonModule,
        FormsModule,
        CustomDateHeurePipe,
        MatIconModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule
    ],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntlService
        }
    ],
    templateUrl: './utilisateurs-root-managment.component.html'
})
export class UtilisateursRootManagmentComponent implements OnInit {
  utilisateurs: UtilisateurListPilotageModel[] = [];
  filteredUtilisateurs: UtilisateurListPilotageModel[] = [];
  filteredUtilisateursAffiches: UtilisateurListPilotageModel[] = [];
  searchTerm: string = '';
  isLoading: boolean = false;
  isLoadingContext: boolean = true;
  includeInvitations: boolean = true;

  pageIndex: number = 0;
  pageSize: number = 10;
  currentPage: number = 0;
  totalItems: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  displayedColumns: string[] = [
    'nom',
    'email',
    'role',
    'dateDerniereConnexion',
    'actions'
  ];

  dataSource!: MatTableDataSource<UtilisateurListPilotageModel>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    public utilisateurService: UtilisateurService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.dataSource = new MatTableDataSource<UtilisateurListPilotageModel>([]);
  }

  ngOnInit(): void {
    this.filteredUtilisateurs = [];
    this.loadContext();
  }

  paginer(event?: PageEvent) {
    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    }
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.filteredUtilisateursAffiches = this.filteredUtilisateurs.slice(startIndex, endIndex);
    this.dataSource.data = this.filteredUtilisateursAffiches;
    this.changeDetectorRef.detectChanges();
  }

  sortDataChange(sortState: Sort) {
    if (sortState.direction) {
      this.dataSource.sort = this.sort;
    }
  }

  private updatePaginatorLength(): void {
    if (this.paginator) {
      this.paginator.length = this.filteredUtilisateurs.length;
      this.totalItems = this.filteredUtilisateurs.length;
      this.paginator.page.emit();
      this.changeDetectorRef.detectChanges();
    }
  }

  refreshDataSource(): void {
    this.filteredUtilisateurs = this.utilisateurs;
    this.dataSource.data = this.filteredUtilisateurs;
    this.updatePaginatorLength();
  }

  private setDefaultFilters(): void {

  }

  loadContext(): void {
    this.isLoadingContext = true;
    this.utilisateurService.obtenirContexte().subscribe({
      next: (res) => {
        this.utilisateurService.contexteUtilisateur = res;
        this.isLoadingContext = false;
      }
    });
  }

  applySearch(): void {
    if (!this.searchTerm || this.searchTerm.length < 3) {
      this.filteredUtilisateurs = [];
      return;
    }

    this.isLoading = true;
    this.utilisateurService.obtenirUtilisateurs(
      this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId,
      this.searchTerm
    ).subscribe({
      next: (res) => {
        this.utilisateurs = res;
        this.applyFilters();
        this.isLoading = false;
      }
    });
  }

  applyFilters(): void {
    this.filteredUtilisateurs = this.includeInvitations
      ? this.utilisateurs
      : this.utilisateurs.filter(u => !u.estInvitationEnAttente);
    this.totalItems = this.filteredUtilisateurs.length;
    this.paginer();
  }

  moveUser(user: UtilisateurListPilotageModel): void {
    const availableEntities = this.utilisateurService.contexteUtilisateur.toutesLesEntites
      .filter(e => !e.estGroupe)
      .sort((a, b) => a.nom.localeCompare(b.nom));

    const dialogRef = this.dialog.open(MoveUserRootDialogComponent, {
      width: '500px',
      data: {
        entities: availableEntities,
        userName: user.nom,
        userId: user.utilisateurId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.utilisateurService.deplacerUtilisateurRoot(result).subscribe({
          next: () => {
            this.snackBar.open(this.translate.instant('USERS_MANAGEMENT.USER_MOVED_SUCCESS'), '', { duration: 3000 });
            this.applySearch();
          },
          error: () => {
            this.snackBar.open(this.translate.instant('USERS_MANAGEMENT.USER_MOVED_ERROR'), '', { duration: 3000 });
          }
        });
      }
    });
  }

  get estRootLectureSeule(): boolean {
    return this.utilisateurService.selectionEntiteUtilisateur?.estRootLectureSeule;
  }
}
